// @noflow
/* global AppMeasurement Visitor ADB */
import { SourceType } from 'Common/models/franchise';
import PlayerManager from '../PlayerManager';
import { PlayerEvent } from '../../../player/utils';

class OmnitureManager extends PlayerManager {
  constructor(delegate) {
    super(delegate);
    this._delegate = delegate;
    this._contentId = delegate._player.props.contentId;
  }

  get enabled() {
    return !!this._videoMetadata.custom_attributes.omniture_enabled;
  }

  get events() {
    return {
      [PlayerEvent.SourceLoaded]: () => {
        if (this.sourceType === SourceType.SLING_DVR) {
          return;
        }
        this.initialize();
      },
      [PlayerEvent.Play]: () => {
        if (this.mediaHeartbeat) this.mediaHeartbeat.trackPlay();
      },
      [PlayerEvent.Paused]: () => {
        if (this.mediaHeartbeat) this.mediaHeartbeat.trackPause();
      },
      [PlayerEvent.PlaybackFinished]: () => {
        if (this.mediaHeartbeat) this.mediaHeartbeat.trackComplete();
      },
      [PlayerEvent.StallStarted]: () => {
        if (this.mediaHeartbeat)
          this.mediaHeartbeat.trackEvent(ADB.va.MediaHeartbeat.Event.BufferStart);
      },
      [PlayerEvent.StallEnded]: () => {
        if (this.mediaHeartbeat) {
          this.mediaHeartbeat.trackEvent(ADB.va.MediaHeartbeat.Event.BufferComplete);
        }
      },
      [PlayerEvent.Seek]: () => {
        if (!this.seeking && this.mediaHeartbeat) {
          this.mediaHeartbeat.trackEvent(ADB.va.MediaHeartbeat.Event.SeekStart);
        }

        this.seeking = true;
      },
      [PlayerEvent.Seeked]: () => {
        this.seeking = false;
        if (this.mediaHeartbeat) {
          this.mediaHeartbeat.trackEvent(ADB.va.MediaHeartbeat.Event.SeekComplete);
        }
      },
      [PlayerEvent.SourceUnloaded]: () => {
        if (this.mediaHeartbeat) this.mediaHeartbeat.trackSessionEnd();
      },
      [PlayerEvent.Destroy]: () => {
        // need to refactor player to use sourceunloaded here
        if (this.mediaHeartbeat) this.mediaHeartbeat.trackComplete();
      },
    };
  }

  initialize() {
    this.setupAppMeasurement();
    this.setupHeartbeat();
    this.fillMediaInfo();
    this.initializeHeartbeat();
  }

  setupAppMeasurement() {
    const { custom_attributes: customAttributes } = this._videoMetadata;
    this.visitor = new Visitor(customAttributes.omniture_visitor_api_marketing_cloud_org_id);
    this.visitor.trackingServer = customAttributes.omniture_visitor_api_tracking_server;
    // if (!App.session.isGuest()) {
    //   this.visitor.setCustomerIDs({
    //     dany94D4C: {
    //       id: App.session.getUuid(),
    //       authState: Visitor.AUTH_STATE_AUTHENTICATED,
    //     },
    //   });
    // }
    this.appMeasurement = new AppMeasurement.AppMeasurement();
    this.appMeasurement.visitor = this.visitor;
    this.appMeasurement.visitorNamespace =
      customAttributes.omniture_visitor_api_marketing_cloud_org_id;
    this.appMeasurement.trackingServer =
      customAttributes.omniture_app_measurement_tracking_server ||
      'dishpurchasingcorporation.sc.omtrdc.net';
    this.appMeasurement.account = customAttributes.omniture_app_measurement_rsid;

    this.appMeasurement.contextData = this.appMeasurement.contextData || {};
    if (customAttributes.omniture_prop75)
      this.appMeasurement.contextData['a.media.playerName'] = customAttributes.omniture_prop75;
    // custom_metadata.tracking doesn't exist, not sure what it was used for.
    // if (this.analyticsInfo && this.analyticsInfo.custom_metadata)
    //   this.appMeasurement.contextData.tracking = this.analyticsInfo.custom_metadata.tracking;
    this.appMeasurement.loadModule('AudienceManagement');
    this.appMeasurement.AudienceManagement.bb({
      partner: 'dish',
      containerNSID: 0,
      uuidCookie: {
        name: 'aam_uuid',
        days: 30,
      },
    });
  }

  setupHeartbeat() {
    this.videoInfo = this.getVideoInfo();
    this.mediaConfig = new ADB.va.MediaHeartbeatConfig();
    this.mediaConfig.trackingServer =
      this._videoMetadata.custom_attributes.omniture_heartbeat_tracking_server ||
      'dishpurchasingcorporation.hb.omtrdc.net';
    this.mediaConfig.playerName = this.videoInfo.playerName;
    this.mediaConfig.channel = this._videoMetadata.networkName;
    this.mediaConfig.appVersion = 'NMP';
    this.mediaConfig.debugLogging = false;
    this.mediaConfig.ssl = true;
    this.mediaConfig.ovp = this._videoMetadata.custom_attributes.omniture_heartbeat_ovp;
    this.mediaInfo = ADB.va.MediaHeartbeat.createMediaObject(
      this.videoInfo.name,
      this.videoInfo.id,
      this.videoInfo.length,
      this.videoInfo.streamType,
    );
    this.mediaInfo.data.playhead = this._delegate.currentTime;
  }

  fillMediaInfo() {
    const standardVideoMetadata = {};
    standardVideoMetadata[ADB.va.MediaHeartbeat.VideoMetadataKeys.SHOW] =
      this._franchise.name || this._franchise.franchiseName;
    standardVideoMetadata[ADB.va.MediaHeartbeat.VideoMetadataKeys.SEASON] = null;
    standardVideoMetadata[ADB.va.MediaHeartbeat.VideoMetadataKeys.EPISODE] = null;
    standardVideoMetadata[
      ADB.va.MediaHeartbeat.VideoMetadataKeys.ASSET_ID
    ] = this._franchise.content_id;
    standardVideoMetadata[ADB.va.MediaHeartbeat.VideoMetadataKeys.GENRE] = this._franchise.genres
      .length
      ? this._franchise.genres[0]
      : null;
    standardVideoMetadata[ADB.va.MediaHeartbeat.VideoMetadataKeys.FIRST_AIR_DATE] = null;
    standardVideoMetadata[
      ADB.va.MediaHeartbeat.VideoMetadataKeys.FIRST_DIGITAL_DATE
    ] = this._franchise.releaseDate;
    standardVideoMetadata[ADB.va.MediaHeartbeat.VideoMetadataKeys.RATING] = this._franchise.rating;
    standardVideoMetadata[ADB.va.MediaHeartbeat.VideoMetadataKeys.ORIGINATOR] = '';
    standardVideoMetadata[ADB.va.MediaHeartbeat.VideoMetadataKeys.NETWORK] = this._franchise
      .networks.length
      ? this._franchise.networks[0].name
      : null;
    standardVideoMetadata[ADB.va.MediaHeartbeat.VideoMetadataKeys.SHOW_TYPE] = '3';
    standardVideoMetadata[ADB.va.MediaHeartbeat.VideoMetadataKeys.AD_LOAD] = '1';
    standardVideoMetadata[ADB.va.MediaHeartbeat.VideoMetadataKeys.MVPD] = '';
    standardVideoMetadata[ADB.va.MediaHeartbeat.VideoMetadataKeys.AUTHORIZED] = '';
    standardVideoMetadata[ADB.va.MediaHeartbeat.VideoMetadataKeys.DAY_PART] = '';
    standardVideoMetadata[ADB.va.MediaHeartbeat.VideoMetadataKeys.FEED] = '';
    standardVideoMetadata[ADB.va.MediaHeartbeat.VideoMetadataKeys.STREAM_FORMAT] = '0';
    this.mediaInfo.setValue(
      ADB.va.MediaHeartbeat.MediaObjectKey.StandardVideoMetadata,
      standardVideoMetadata,
    );
  }

  previousCurrentTime = 0;

  initializeHeartbeat() {
    this.mediaHeartbeatDelegate = new ADB.va.MediaHeartbeatDelegate();
    this.mediaHeartbeatDelegate.getCurrentPlaybackTime = () => {
      if (this._delegate._player.playerView) {
        const currentTime = this._delegate.currentTime;
        this.previousCurrentTime = currentTime;
        return currentTime;
      }
      return this.previousCurrentTime;
    };
    this.mediaHeartbeatDelegate.getQoSObject = () => null;

    this.mediaHeartbeat = new ADB.va.MediaHeartbeat(
      this.mediaHeartbeatDelegate,
      this.mediaConfig,
      this.appMeasurement,
    );

    this.mediaHeartbeat.trackSessionStart(this.mediaInfo, this.appMeasurement.contextData);
  }

  getVideoInfo() {
    return {
      id: this._contentId.toString(),
      name: this._delegate._player.props.videoMetadata.showName,
      playerName:
        this._videoMetadata.custom_attributes.omniture_prop75 ||
        `${this._videoMetadata.networkName}|desktop|dish dany`,
      length: this._videoMetadata.clipDurationSeconds,
      streamType: this.sourceType,
      playhead: this._delegate.currentTime,
    };
  }
}

export default OmnitureManager;
